import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import QueueBar from '../../QueueBar';
import { calculateRemToPx } from '../../../../utils/newBedStatusUtil';
import { BS_TOOLBAR_STATUS } from '../../../../comm/constant';
import EAdmissionToolbar from '../EADViewFloorPlan/components/EAdmissionToolbar';
import EADViewFloorPlan from '../EADViewFloorPlan';

const useStyles = makeStyles(() => ({
  container: ({ floorPlanDimensions }) => ({
    display: 'flex',
    gridGap: '0.857rem',
    margin: '0.5rem 0',
    height: floorPlanDimensions?.height,
  }),
  toolbar: {
    marginLeft: '-1.9rem',
  },
  floorplanPanel: ({ floorPlanDimensions }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: floorPlanDimensions?.width,
    height: '100%',
    minWidth: 0,
    minHeight: 0,
  }),
  floorplanFilter: {
    backgroundColor: '#FFFFFF',
    borderRadius: '0.9375rem 0.9375rem 0 0',
    padding: '0.5rem 0.5rem 0',
  },
  floorplanBox: ({ toolStatus, hasFloorplanHeader }) => {
    let style = {
      cursor: 'auto',
      userSelect: 'auto',
      padding: '0.5rem',
      borderRadius: hasFloorplanHeader ? '0 0 0.9375rem 0.9375rem' : '0.9375rem',
      flex: 1,
      width: '100%',
      minHeight: 0,
      backgroundColor: '#FFFFFF',
      overflow: 'hidden',
      touchAction: 'none',
      boxSizing: 'border-box',
    };

    if (toolStatus.type === BS_TOOLBAR_STATUS.MOUSE) {
      style.cursor = 'auto';
      style.userSelect = 'auto';
    } else if (toolStatus.type === BS_TOOLBAR_STATUS.MOVE) {
      style.cursor = 'grab';
      style.userSelect = 'none';
    }

    return style;
  },
  floorplanScrollSpace: ({ floorPlanSize, toolStatus }) => ({
    width: floorPlanSize.width > 0 ? Math.ceil(floorPlanSize.width * toolStatus.zoom) : '100%',
    height: floorPlanSize.height > 0 ? Math.ceil(floorPlanSize.height * toolStatus.zoom) : '100%',
    minWidth: '100%',
    minHeight: '100%',
    position: 'relative',
  }),
  floorplanScaleBox: ({ floorPlanSize, toolStatus }) => ({
    width: floorPlanSize.width > 0 ? floorPlanSize.width : '100%',
    height: floorPlanSize.height > 0 ? floorPlanSize.height : '100%',
    transform: `scale(${toolStatus.zoom})`,
    transformOrigin: 'left top',
  }),
}));

// floorplan的滚动缓冲高度和宽度,以防算出来有误差
const FLOORPLAN_SCROLL_BUFFER = 120;
const DEFAULT_FLOORPLAN_ZOOM = 1;

const getStoredFloorplanZoom = (storageKey) => {
  if (!storageKey) {
    return DEFAULT_FLOORPLAN_ZOOM;
  }
  const storageValue = sessionStorage.getItem(storageKey);
  if (!storageValue) {
    return DEFAULT_FLOORPLAN_ZOOM;
  }
  const zoom = Number(storageValue);
  if (!Number.isFinite(zoom) || zoom < 0.5 || zoom > 2) {
    return DEFAULT_FLOORPLAN_ZOOM;
  }

  return zoom;
};

export default function AdmissionLocationView({
  headerRef,
  contentHeight,
  filterNode,
  statisticNode,
  queuebarList,
  cubicleList,
  handleAssignPatient,
  handleDeleteBedPatient,
  handleOpenPatientDetail,
  handleUpdateCubicle,
  queueTitle = 'Queue',
  queuebarStorageKey,
  floorplanZoomStorageKey,
  viewType = '',
  showDetails = true,
  patientInfoViewType = '',
}) {
  const [showQueuebar, setShowQueuebar] = useState(JSON.parse(sessionStorage.getItem(queuebarStorageKey)) ?? false);
  const fullScreen = useSelector((state) => state.fullScreenReducer);
  const [floorPlanDimensions, setFloorPlanDimensions] = useState({
    height: 0,
    width: 0,
  });
  const [toolStatus, setToolStatus] = useState({
    type: BS_TOOLBAR_STATUS.MOVE,
    zoom: getStoredFloorplanZoom(floorplanZoomStorageKey),
    reset: 0,
  });
  const queuebarRef = useRef(null);
  const isDraggingRef = useRef(false);
  const floorplanRef = useRef(null);
  const startPositionRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedBed, setSelectedBed] = useState(null);
  const floorPlanSize = useMemo(() => {
    let width = 0;
    let height = 0;
    let top = 0;
    let left = 0;

    _.forEach(cubicleList, (item) => {
      if (Number(item.x) + Number(item.width) > Number(width)) {
        width = Number(item.x) + Number(item.width);
      }

      if (Number(item.y) + Number(item.height) > Number(height)) {
        height = Number(item.y) + Number(item.height);
      }

      if (Number(item.y) < 0 && Number(item.y) < Number(top)) {
        top = -Number(item.y);
      }

      if (Number(item.x) < 0 && Number(item.x) < Number(left)) {
        left = -Number(item.x);
      }
    });

    // 给floorplan的宽高都加一定的额外滚动缓冲区，以防算出来的宽高有误差
    return {
      width: width + left + FLOORPLAN_SCROLL_BUFFER,
      height: height + top + FLOORPLAN_SCROLL_BUFFER,
    };
  }, [cubicleList]);
  const classes = useStyles({
    floorPlanDimensions,
    floorPlanSize,
    toolStatus,
    fullScreen,
    hasFloorplanHeader: Boolean(filterNode || statisticNode),
  });

  // 从Queuebar中选中病人
  const handleSelectPatient = useCallback((patient) => {
    setSelectedPatient((prev) => {
      if (patient?.eaId === prev?.eaId) {
        return null;
      }

      return patient;
    });
  }, []);

  // 在Cubicle中选中病床
  const handleSelectBed = useCallback(({ cubicleId, bedData, hasPatient }) => {
    // 还没有床号的床不能被选中
    // if (bedData?.bedName === '') return;

    setSelectedBed((prev) => {
      if (prev?.cubicleId === cubicleId && prev?.key === bedData?.key) {
        return null;
      }
      // 如果选中了可用的病床又没有打开Queuebar的时候，自动打开Queuebar
      if (!hasPatient) {
        setShowQueuebar(true);
      }
      return {
        ...bedData,
        cubicleId,
        hasPatient,
      };
    });
  }, []);

  const handleDeleteSelectedBedPatient = useCallback(
    async (params) => {
      await handleDeleteBedPatient({
        ...params,
        setSelectedBed,
      });
    },
    [handleDeleteBedPatient]
  );

  // 当选了Queuebar的病人和cubicle的病床时，绑定病人到病床上
  useEffect(() => {
    handleAssignPatient({
      selectedPatient,
      selectedBed,
      setSelectedPatient,
      setSelectedBed,
    });
  }, [handleAssignPatient, selectedPatient, selectedBed]);

  useEffect(() => {
    if (floorplanZoomStorageKey) {
      sessionStorage.setItem(floorplanZoomStorageKey, String(toolStatus.zoom));
    }
  }, [floorplanZoomStorageKey, toolStatus.zoom]);

  const handleMouseDown = useCallback(
    (event) => {
      const isPopup = document.getElementsByClassName('MuiDialog-root').length > 0;
      if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE || isPopup) {
        return;
      }
      event.preventDefault();
      isDraggingRef.current = true;
      const position = {
        x: event.clientX,
        y: event.clientY,
        scrollLeft: floorplanRef.current.scrollLeft,
        scrollTop: floorplanRef.current.scrollTop,
      };

      startPositionRef.current = position;
    },
    [toolStatus]
  );

  const handleTouchStart = useCallback(
    (event) => {
      const isPopup = document.getElementsByClassName('MuiDialog-root').length > 0;
      if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE || event.changedTouches.length > 1 || isPopup) {
        return;
      }
      event.preventDefault();
      isDraggingRef.current = true;
      const position = {
        x: event.changedTouches[0].clientX,
        y: event.changedTouches[0].clientY,
        scrollLeft: floorplanRef.current.scrollLeft,
        scrollTop: floorplanRef.current.scrollTop,
      };
      startPositionRef.current = position;
    },
    [toolStatus]
  );

  const handleMouseMove = useCallback(
    (event) => {
      const isPopup = document.getElementsByClassName('MuiDialog-root').length > 0;
      if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE || isPopup) {
        return;
      }
      // 使用 ref 检查拖拽状态，避免依赖 state 导致函数重新创建
      if (isDraggingRef.current && floorplanRef.current) {
        event.preventDefault();
        const startPos = startPositionRef.current;
        const deltaX = event.clientX - startPos.x;
        const deltaY = event.clientY - startPos.y;
        const scrollLeft = startPos.scrollLeft - deltaX;
        const scrollTop = startPos.scrollTop - deltaY;
        floorplanRef.current.scroll(scrollLeft, scrollTop);
      }
    },
    [toolStatus]
  );

  const handleTouchMove = useCallback(
    (event) => {
      const isPopup = document.getElementsByClassName('MuiDialog-root').length > 0;
      if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE || event.changedTouches.length > 1 || isPopup) {
        return;
      }
      // 使用 ref 检查拖拽状态，避免依赖 state 导致函数重新创建
      if (isDraggingRef.current && floorplanRef.current) {
        event.preventDefault();
        const startPos = startPositionRef.current;
        const deltaX = event.changedTouches[0].clientX - startPos.x;
        const deltaY = event.changedTouches[0].clientY - startPos.y;
        const scrollLeft = startPos.scrollLeft - deltaX;
        const scrollTop = startPos.scrollTop - deltaY;
        floorplanRef.current.scroll(scrollLeft, scrollTop);
      }
    },
    [toolStatus]
  );

  const handleMouseUp = useCallback(() => {
    if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE) {
      return;
    }
    isDraggingRef.current = false;
  }, [toolStatus]);

  const handleMouseLeave = useCallback(() => {
    if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE) {
      return;
    }
    isDraggingRef.current = false;
  }, [toolStatus]);

  useEffect(() => {
    if (floorplanRef.current) {
      floorplanRef.current.scroll(0, 0);
    }
  }, [toolStatus.reset]);

  // 监听 window resize 和 ref 变化
  useEffect(() => {
    // 计算并更新高度
    const calculatedHeight = () => {
      try {
        // 页面高度
        const pageHeight = document.documentElement?.clientHeight || window.innerHeight || 0;
        const pageWidth = document.documentElement?.clientWidth || window.innerWidth || 0;
        // header 高度
        const headerHeight = headerRef?.current ? headerRef.current.getBoundingClientRect().height : 0;
        const availableHeight =
          contentHeight > 0
            ? Math.round(contentHeight - calculateRemToPx(1))
            : Math.round(pageHeight - headerHeight - (fullScreen ? 0 : calculateRemToPx(4.375)) - calculateRemToPx(1));
        // queuebar 宽度
        const queuebarWidth = queuebarRef?.current ? queuebarRef.current.getBoundingClientRect().width : 0;
        const availableWidth = Math.round(
          pageWidth - queuebarWidth - (fullScreen ? 0 : calculateRemToPx(6.25)) - calculateRemToPx(0.857)
        );

        setFloorPlanDimensions({ height: availableHeight, width: availableWidth });
      } catch {
        setFloorPlanDimensions({ height: 0, width: 0 });
      }
    };

    calculatedHeight();
    window.addEventListener('resize', calculatedHeight);

    return () => {
      window.removeEventListener('resize', calculatedHeight);
    };
  }, [contentHeight, headerRef, queuebarRef, showQueuebar, fullScreen]);

  return (
    <Box className={classes.container}>
      <Box display="flex" position="relative">
        <QueueBar
          ref={queuebarRef}
          showQueuebar={showQueuebar}
          setShowQueuebar={setShowQueuebar}
          queuePatientList={queuebarList}
          selectedPatient={selectedPatient}
          handleSelectPatient={handleSelectPatient}
          handleOpenPatientDetail={handleOpenPatientDetail}
          queueTitle={queueTitle}
          queuebarStorageKey={queuebarStorageKey}
          showDetails={showDetails}
          patientInfoViewType={patientInfoViewType}
        />
        <EAdmissionToolbar type="landing" status={toolStatus} setStatus={setToolStatus} className={classes.toolbar} />
      </Box>
      <Box className={classes.floorplanPanel}>
        {filterNode && (
          <Box className={classes.floorplanFilter}>
            {/* Location View顶部扩展区，AED View先放filter。 */}
            {filterNode}
          </Box>
        )}
        {statisticNode}
        <Box
          ref={floorplanRef}
          className={classes.floorplanBox}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleMouseUp}
          onTouchCancel={handleMouseLeave}
        >
          <Box className={classes.floorplanScrollSpace}>
            <Box className={classes.floorplanScaleBox}>
              <Suspense fallback={<div />}>
                <EADViewFloorPlan
                  toolStatus={toolStatus}
                  cubicleList={cubicleList}
                  selectedBed={selectedBed}
                  handleSelectBed={handleSelectBed}
                  handleDeleteBedPatient={handleDeleteSelectedBedPatient}
                  handleOpenPatientDetail={handleOpenPatientDetail}
                  handleUpdateCubicle={handleUpdateCubicle}
                  // 标记当前是 AED/SOPD，用来控制 popup 的颜色选项。
                  viewType={viewType}
                  showDetails={showDetails}
                  patientInfoViewType={patientInfoViewType}
                />
              </Suspense>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
